/**
 * The enums of server_pray's reels and clips routes (praycom/server_pray#19207), as the server code defines them.
 * contract.vitest.ts pins every value, so a drift on either side fails a test.
 */

/** §4.1: `type` on rows, detail and the list filter; `statusCounts.pps` counts the first. */
export const CLIP_TYPES = ['opus', 'pps'] as const;

/** §4.2 */
export const TASK_KINDS = ['generate', 'rerender'] as const;

/** §4.3 */
export const TASK_STATUSES = ['generating', 'completed', 'failed'] as const;
export const REEL_STAGES = ['preparing', 'finding_moment', 'creating', 'finishing'] as const;

/** §4.4 */
export const CLIP_STATUSES = ['ready', 'generating', 'failed', 'posted', 'scheduled', 'publish_failed'] as const;
export const POST_STATUSES = ['posted', 'scheduled', 'publish_failed'] as const;
export const PLATFORM_STATUSES = ['published', 'failed', 'pending'] as const;
export const PLATFORM_ACTIONS = ['retry', 'reconnect_account'] as const;

/** §4.5: a task's `failedReason`; any other value (a plain PPS code) is shown as the generic failure. */
export const FAILED_REASONS = [
  'PPS_REJECTED',
  'PPS_UNAVAILABLE',
  'PPS_APPROVAL_REQUIRED',
  'QUOTE_EXCEEDS_CAP',
  'DEADLINE_EXCEEDED',
  'COPY_FAILED',
  'CANCELLED',
  'UPLOAD_FAILED',
  'ANALYSIS_FAILED',
  'NOT_ENOUGH_MOMENTS',
  'CAPTION_TIMING_LOCKED',
  'PPS_REEL_DELETED',
  'CHARGE_FAILED',
] as const;

/** §4.6 */
export const REEL_WARNINGS = [
  'LOGO_END_CARD_ONLY',
  'CAPTION_POSITION_UNSUPPORTED',
  'CAPTION_BACKGROUND_UNSUPPORTED',
  'CAPTION_TIMING_LOCKED',
  'TAGS_RERANK_ONLY',
  'CAPTION_LAYOUT_NOT_DRAWN',
  'CAPTION_UPPERCASE_POP_ONLY',
  'CAPTION_COLOR_ADJUSTED',
  'RENDER_NOTE',
  'LOGO_UNAVAILABLE',
] as const;

/** §4.7: the credit quote and confirmation `operation` of Generate and of a priced re-render. */
export const REEL_CREDIT_OPERATIONS = { generate: 'reels', rerender: 'reel_rerender' } as const;

/** §1.1: the page view's `unavailableReason` */
export const UNAVAILABLE_REASONS = ['REELS_DISABLED', 'REELS_NOT_CONFIGURED'] as const;

/** §1.1 / §3: the page view's `ineligibleReason` and REELS_SOURCE_UNSUPPORTED's `details.reason` */
export const SOURCE_REASONS = ['no_uploaded_video', 'unsupported_format', 'too_large'] as const;

/** §1.4 */
export const CLIPS_SORTS = ['created_at_desc', 'created_at_asc', 'title'] as const;
export const CLIPS_LIMIT_MAX = 50;

/** §1.7 / §2.6 */
// A RegExp object, not a literal: the `u` flag (for \p{L} letters in any script) is newer than Studio's TS target.
export const TAG_RULE = {
  maxTags: 10,
  maxLength: 40,
  pattern: new RegExp("^[\\p{L}\\p{N}][\\p{L}\\p{N} '&-]*$", 'u'),
} as const;
export const CAPTION_RULE = { maxCues: 400, maxCueLength: 80, maxWords: 8, maxWordLength: 40 } as const;

/** §5: the global server switch, an entry of GET /web/toggles */
export const REELS_TOGGLE = 'reels_enabled';

export type ClipTypeValue = (typeof CLIP_TYPES)[number];
export type ReelStageValue = (typeof REEL_STAGES)[number];
export type ReelWarning = (typeof REEL_WARNINGS)[number];
